import { useState, useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import "react-calendar/dist/Calendar.css";
import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";

//MUI
import styled from "@emotion/styled";
import Typography from "@mui/material/Typography";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";

//components
import EventsCardAI from "../Cards/EventsCardAI";
import IndividualEventDetails from "./IndividualEventDetails";
import AIBreadCrumbs from "../Common/AIBreadCrumbs";
import NoData from "../Common/NoData";

//apis
import { getStateList, getEventsByDate } from "../../apis/events.js";

const Container = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  background: "#F6FAFE",
  flexDirection: "column",
  alignItems: "center",

  "& .header_filter": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "0 1.5rem",
    marginBottom: "0.5rem",
    [theme.breakpoints.down("xl")]: {
      width: "80%",
    },
    [theme.breakpoints.down("lg")]: {
      width: "80%",
    },
    [theme.breakpoints.down("md")]: {
      width: "85%",
    },
    "& .react-daterange-picker": {
      border: "none",
      boxShadow: "none !important",
      outline: "none !important",
      background: "#fff",
    },

    "& .left_header .heading_text": {
      color: "#052564ff",
      fontSize: "20px",
      fontWeight: "600",
      margin: "0 0 0 1rem",
    },
    "& .right_header": {
      display: "flex",
      alignItems: "center",
      gap: "1rem",
      position: "relative",
      marginRight: "1rem",
      "& .dropdown, & .date_filter": {
        padding: "0.4rem 0.6rem",
        borderRadius: "6px",
        border: "1px solid #ccc",
        fontSize: "14px",
      },
    },
  },
  "& .events_container": {
    width: "100%",
    height: "auto",
    minHeight: "600px",
    overflowY: "visible",
    overflowX: "hidden",
    display: "flex",
    justifyContent: "center",
    paddingTop: "1rem",
    paddingBottom: "1rem",

    "& .main_block": {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "0.5rem",
      [theme.breakpoints.down("xl")]: {
        width: "80%",
      },
      [theme.breakpoints.down("lg")]: {
        width: "80%",
      },
      [theme.breakpoints.down("md")]: {
        width: "85%",
      },
      "& .events_heading": {
        color: "#1B1C1E",
        fontSize: "18px",
        fontWeight: "700",
      },
      "& .events_list": {
        width: "100%",
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: "1rem",
        padding: "0 2rem",
        columnGap: "1.5rem",
        rowGap: "2rem",
        justifyContent: "center",
        "& > div": {
          width: "100%",
          maxWidth: "100%",
          height: "200px",
          display: "flex",
          flexDirection: "column",
        },
        "& .no-data": {
          gridColumn: "1 / -1",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: "500px",
          textAlign: "center",
        }

      },
    },
  },
  "& .state_dropdown_wrapper": {
    width: "auto",
    maxWidth: "210px",
    margin: 0,
    flexShrink: 0,
  },

  "& .dropdown_btn": {
    padding: "0.4rem 0.6rem",
    borderRadius: "6px",
    border: "1px solid #ccc",
    background: "#fff",
    cursor: "pointer",
    fontSize: "14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
  },

  "& .state_dropdown": {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: -"30px",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: "6px",
    width: "260px",
    zIndex: 50,
    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
    padding: "8px",
  },
  "& .clear_state_icon": {
    marginLeft: "8px",
    cursor: "pointer",
    fontWeight: "bold",
    color: "#052564",
    fontSize: "14px",
  },

  "& .state_search": {
    width: "100%",
    padding: "0.45rem 0.5rem",
    marginBottom: "6px",
    border: "1px solid #eee",
    borderRadius: "6px",
    outline: "none",
    fontSize: "14px",
  },

  "& .state_list": {
    maxHeight: "220px",
    overflowY: "auto",
  },

  "& .state_option": {
    padding: "0.5rem",
    cursor: "pointer",
    position: "relative",
    borderRadius: "4px",
  },

  "& .state_option:hover": {
    background: "#f0f0f0",
  },

  "& .state_option.active": {
    background: "#e6f0ff",
  },

  "& .state_option .filter_tab": {
    position: "absolute",
    left: "100%",
    top: 0,
    marginLeft: "8px",
    width: "200px",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "10px",
    zIndex: 60,
    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    gap: "0.6rem",
  },
  "& .date_filter": {
    marginLeft: "0",
    flexShrink: 0,
    maxWidth: "220px",
    "& .react-daterange-picker__wrapper": {
      border: "none"
    },
    "& .rdrDateDisplayWrapper": {
      border: "2px solid #1976d2",
      borderRadius: "8px",
      background: "#fff",
    },

    "& .rdrDateDisplayItem": {
      border: "none !important",
      boxShadow: "none !important",
    },

    "& .rdrDateDisplayItem input": {
      border: "none !important",
      outline: "none !important",
      background: "transparent",
      color: "#052564",
      fontWeight: 500,
      fontSize: "14px",
      textAlign: "center",
    },
  }

}));

const IndividualEventContainer = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
}));

const ExploreEvents = () => {
  const { eventId } = useParams();
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef(null);
  const dropdownRef = useRef(null);
  const [selectedState, setSelectedState] = useState("");

  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 1);
  const endDate = new Date(today);
  endDate.setDate(today.getDate());

  const [dateRange, setDateRange] = useState([startDate, endDate]);
  const [setDisplayStart] = useState(format(startDate, "dd-MM-yyyy"));
  const [setDisplayEnd] = useState(format(endDate, "dd-MM-yyyy"));
  const [stateSearch, setStateSearch] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [setHoveredState] = useState(null);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const filteredEvents = events;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setHoveredState(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  useEffect(() => {
    const fetchEvents = async () => {
      if (loading) return;
      setLoading(true);

      const start = dateRange?.[0] ? format(dateRange[0], "yyyy-MM-dd") : "";
      const end = dateRange?.[1] ? format(dateRange[1], "yyyy-MM-dd") : "";

      const res = await getEventsByDate(page, start, end, selectedState || "");
      if (res.data) {
        setEvents(prev =>
          page === 1 ? res.data.results || [] : [...prev, ...(res.data.results || [])]
        );
        setHasMore(!!res.data.next);
      }
      setLoading(false);
    };
    if (dateRange?.[0] && dateRange?.[1]) fetchEvents();
  }, [page, selectedState, dateRange, resetTrigger]);


  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await getStateList();
        if (response.data) {
          const allowedStates = [
            "Andhra Pradesh",
            "Assam",
            "Bihar",
            "Kerala",
            "National",
            "Tamil Nadu",
            "West Bengal",
          ];

          const filteredStates = response.data.filter((state) =>
            allowedStates.includes(state)
          );
          // Sort alphabetically
          filteredStates.sort((a, b) => a.localeCompare(b));

          setStates(filteredStates);
        }
      } catch (err) {
        console.error("Failed to fetch states", err);
      }
    };
    fetchStates();
  }, []);


  const lastEventRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const handleEventSelect = (id) => {
    // navigate(`/app/explore_ai/events/${id}`);
  };

  const isDateRangeMoreThan7Days = () => {
    if (!dateRange?.[0] || !dateRange?.[1]) return false;
    const diffTime = Math.abs(dateRange[1] - dateRange[0]);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 7;
  };

  const handleDateChange = async (newDate) => {
    setDateRange(newDate);
    setPage(1);
    setEvents([]);
    setHasMore(true);
    setResetTrigger(prev => prev + 1);

    if (newDate[0]) setDisplayStart(format(newDate[0], "dd-MM-yyyy"));
    else setDisplayStart("");

    if (newDate[1]) setDisplayEnd(format(newDate[1], "dd-MM-yyyy"));
    else setDisplayEnd("");

    if (newDate[0] && newDate[1]) {
      const diffTime = Math.abs(newDate[1] - newDate[0]);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 7) {
        alert("Exceed 7 days. Please select within 7 days.");
        setDateRange([newDate[0], null]);
        setDisplayEnd("");
        return;
      }
      setEvents([]);
      setPage(1);

      const start = format(newDate[0], "yyyy-MM-dd");
      const end = format(newDate[1], "yyyy-MM-dd");

      const res = await getEventsByDate(1, start, end, selectedState);
      if (res.data) setEvents(res.data.results || res.data);
    }
  };

  return (
    <>
      {!eventId ? (
        <Container>
          <AIBreadCrumbs />
          <div className="header_filter">
            <div className="left_header">
              <Typography className="heading_text">Trending Events</Typography>
            </div>
            <div className="right_header">

              {/* States dropdown button + popup */}
              <div className="state_dropdown_wrapper" ref={dropdownRef}>
                <button
                  className="dropdown_btn"
                  onClick={() => setIsDropdownOpen((p) => !p)}
                  aria-expanded={isDropdownOpen}
                >
                  <span style={{ flex: 1, textAlign: "left" }}>
                    {selectedState || "Select Geography"}
                  </span>

                  {selectedState && (
                    <span

                      onClick={async (e) => {
                        e.stopPropagation();
                        setSelectedState("");
                        setPage(1);
                        setEvents([]);
                        setHasMore(true);
                        setResetTrigger(prev => prev + 1);

                        const start = dateRange?.[0] ? format(dateRange[0], "yyyy-MM-dd") : "";
                        const end = dateRange?.[1] ? format(dateRange[1], "yyyy-MM-dd") : "";
                      }}

                      style={{
                        marginLeft: "auto",
                        cursor: "pointer",
                        fontWeight: "bold",
                        color: "#052564",
                        padding: "0 4px",
                      }}
                    >
                      ×
                    </span>
                  )}

                  {isDropdownOpen ? (
                    <ArrowDropUpIcon fontSize="small" />
                  ) : (
                    <ArrowDropDownIcon fontSize="small" />
                  )}
                </button>

                {isDropdownOpen && (
                  <div className="state_dropdown" role="menu">
                    <input
                      type="text"
                      className="state_search"
                      placeholder="Search State..."
                      value={stateSearch}
                      onChange={(e) => setStateSearch(e.target.value)}
                    />
                    <div className="state_list">
                      {states
                        .filter((st) =>
                          st.toLowerCase().includes(stateSearch.toLowerCase())
                        )
                        .map((st) => (
                          <div
                            key={st}
                            className={`state_option ${selectedState === st ? "active" : ""}`}
                            onMouseEnter={() => setHoveredState(st)}
                            onMouseLeave={() => setHoveredState(null)}

                            onClick={() => {
                              setSelectedState(st);
                              setIsDropdownOpen(false);
                              setPage(1);
                              setEvents([]);
                              setHasMore(true);
                            }}
                          >
                            {st}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="date_filter">
                <DateRangePicker
                  onChange={handleDateChange}
                  value={dateRange}
                  clearIcon={null}
                  calendarIcon={null}
                  format="dd-MM-yyyy"
                  showLeadingZeros={true}
                  dayPlaceholder="dd"
                  monthPlaceholder="mm"
                  yearPlaceholder="yyyy"
                />
              </div>
            </div>
          </div>

          <div className="events_container">
            <div className="main_block">
              <div className="events_list">
                {isDateRangeMoreThan7Days() ? (
                  <div className="no-data">
                    <NoData msg="Please select a date range of 7 days or less" />
                  </div>
                ) : loading ? (
                  <div className="no-data">
                    <NoData msg="Loading..." />
                  </div>
                ) : filteredEvents.length > 0 ? (
                  filteredEvents.map((item, idx) => {
                    if (idx === filteredEvents.length - 1) {
                      return (
                        <EventsCardAI
                          key={item.event_id}
                          ref={lastEventRef}
                          data={item}
                          handleEventSelect={() => handleEventSelect(item.event_id)}
                        />
                      );
                    }
                    return (
                      <EventsCardAI
                        key={item.event_id}
                        data={item}
                        handleEventSelect={() => handleEventSelect(item.event_id)}
                      />
                    );
                  })
                ) : (
                  <div className="no-data">
                    <NoData msg="No events found." />
                  </div>
                )}

              </div>
            </div>
          </div>
        </Container>
      ) : (
        <IndividualEventContainer>
          <IndividualEventDetails
            eventId={eventId}
            eventsList={filteredEvents}
          />
        </IndividualEventContainer>
      )}
    </>
  );
};

export default ExploreEvents;
